/**
 * The Skills pane's icons — lucide's paths, inline, as the chat components
 * carry theirs (no icon package in this bundle; the CSP has nothing to fetch).
 */
import type { ReactNode } from "react";

function Svg({ size = 16, children, strokeWidth = 2 }: { size?: number; children: ReactNode; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

/** Processor icon for model selection. */
export const CpuIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" /></Svg>
);
/** lucide `Wrench` — the default category. */
export const WrenchIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></Svg>
);
/** lucide `PlugZap` — MEP. */
export const PlugZapIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M6.3 20.3a2.4 2.4 0 0 0 3.4 0L12 18l-6-6-2.3 2.3a2.4 2.4 0 0 0 0 3.4Z" /><path d="m2 22 3-3" /><path d="M7.5 13.5 10 11" /><path d="M10.5 16.5 13 14" /><path d="m18 3-4 4h6l-4 4" /></Svg>
);
/** lucide `Building2` — structure. */
export const BuildingIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" /><path d="M10 6h4" /><path d="M10 10h4" /><path d="M10 14h4" /><path d="M10 18h4" /></Svg>
);
/** lucide `MousePointer2` — Revit operations. */
export const PointerIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" /></Svg>
);
export const FolderIcon = ({ size, filled = false }: { size?: number; filled?: boolean }) => (
  <svg width={size ?? 16} height={size ?? 16} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} fillOpacity={filled ? 0.25 : 1} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </svg>
);
export const FolderPlusIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M12 10v6" /><path d="M9 13h6" /><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" /></Svg>
);
export const FolderInputIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1" /><path d="M2 13h10" /><path d="m9 16 3-3-3-3" /></Svg>
);
export const SearchIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></Svg>
);
export const ChevronLeftIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="m15 18-6-6 6-6" /></Svg>
);
export const ChevronDownIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="m6 9 6 6 6-6" /></Svg>
);
export const ChevronRightIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="m9 18 6-6-6-6" /></Svg>
);
export const MoreIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></Svg>
);
export const BoxIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></Svg>
);
export const ClockIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></Svg>
);
export const PlayIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><polygon points="6 3 20 12 6 21 6 3" /></Svg>
);
export const TrashIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></Svg>
);
export const CloudUploadIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M12 13v8" /><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" /><path d="m8 17 4-4 4 4" /></Svg>
);
export const AlertIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" /></Svg>
);
export const LoaderIcon = ({ size }: { size?: number }) => (
  <svg className="skills-spin" width={size ?? 16} height={size ?? 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);
export const CheckIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M20 6 9 17l-5-5" /></Svg>
);
export const XIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Svg>
);
export const RefreshIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></Svg>
);
export const CodeIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></Svg>
);
export const HistoryIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></Svg>
);
export const PlusIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M5 12h14" /><path d="M12 5v14" /></Svg>
);
export const PencilIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" /><path d="m15 5 4 4" /></Svg>
);
export const SaveIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" /><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" /><path d="M7 3v4a1 1 0 0 0 1 1h7" /></Svg>
);
/** lucide `Layers` — an AutoCAD skill's cross-version build: the same skill on more versions. */
export const LayersIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" /></Svg>
);
export const UploadIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></Svg>
);
/** lucide `Funnel` — the year filter, standing in for its label. */
export const FilterIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" /></Svg>
);
/** lucide `ArrowDownUp` — the sort control, standing in for its label. */
export const SortIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="m21 8-4-4-4 4" /><path d="M17 4v16" /></Svg>
);
export const InfoIcon = ({ size }: { size?: number }) => (
  <Svg size={size}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></Svg>
);
export const RevitGlyph = ({ size }: { size?: number }) => (
  <Svg size={size} strokeWidth={1.7}><path d="M4 20V4h8a4 4 0 0 1 0 8H8" /><path d="m12 12 5 8" /></Svg>
);
export const AutocadGlyph = ({ size }: { size?: number }) => (
  <Svg size={size} strokeWidth={1.7}><path d="m4 20 8-16 8 16" /><path d="M8 14h8" /></Svg>
);
